import { useOrganisation } from '@/app/redux/hooks/organisation';
import UpgradeButton from '@/common/ui/UpgradeButton';
import { getTrialDaysRemaining } from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { Progress } from '@shadcn/components/progress';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export default function UpgradeCard() {
  const navigate = useNavigate();
  const { organisation } = useOrganisation();

  const plan = organisation?.license.plan;

  const trialStats = useMemo(() => {
    if (plan !== 'trial' || !organisation) return null;
    const total = Math.round(
      dayjs(organisation.license.trialEndDate).diff(
        dayjs(organisation.createdAt),
        'day',
        true,
      ),
    );
    const remaining = getTrialDaysRemaining(organisation.license.trialEndDate);
    const used = Math.max(0, Math.min(total, total - remaining));
    return {
      total,
      remaining,
      used,
      progress: total > 0 ? (used / total) * 100 : 100,
    };
  }, [plan, organisation]);

  const handleUpgradeClick = () => {
    trackMixpanelEvent('Clicked Upgrade from Dashboard', {
      source: 'upgrade-card',
      variant: plan,
      ...(trialStats && {
        trialDaysRemaining: trialStats.remaining,
      }),
    });
    navigate('/pricing');
  };

  return (
    <div className="mx-1.5 rounded-xl border border-border bg-background p-3">
      <p className="text-sm font-semibold text-foreground/80 mb-1">
        {plan === 'trial'
          ? 'Spellcaster Pro Trial 💫'
          : 'You are on the Spellcaster Free Plan'}
      </p>

      {trialStats && (
        <>
          <p className="text-xs font-medium text-foreground/80 mb-2">
            {trialStats.used} of {trialStats.total} days used
          </p>
          <Progress
            value={trialStats.progress}
            className="mb-2 h-1.5 bg-accent"
          />
        </>
      )}

      <p className="text-xs text-muted-foreground mb-3">
        {plan === 'trial'
          ? 'Upgrade to Spellcaster Pro to continue using the Spellcaster game, data and other features'
          : 'Upgrade to Spellcaster Pro to enjoy the Spellcaster game, data, assessments and more.'}
      </p>

      <UpgradeButton onClick={handleUpgradeClick} className="w-full" />
    </div>
  );
}
